import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllCharacters } from "../../redux/slices/usersSlice";
import { RxCross1 } from "react-icons/rx";
import { FaCircle, FaRegCircle } from "react-icons/fa6";
import {
  addParticipants,
  getAllParticipants,
} from "../../redux/slices/gameSlice";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { LineWave } from "react-loader-spinner";

const ParticipantPopup = ({ onClose, participateId }) => {
  const dispatch = useDispatch();
  const { characters, isLoading } = useSelector((state) => state.usersReducer);
  const { participantsLoading } = useSelector((state) => state.gameReducer);
  const [characterId, setCharacterId] = useState("");
  const [query, setQuery] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const totalCharacterPoint = 6;
  const { id } = useParams();

  const resetStateOnClear = () => {
    setErrorMsg("");
    setHasSearched(false);
    setCharacterId("");
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceTimeout) clearTimeout(debounceTimeout);
    if (!value) {
      resetStateOnClear();
      return;
    }

    resetStateOnClear();

    const newTimeout = setTimeout(() => {
      if (value.length !== 8) {
        setErrorMsg("Membership ID must be exactly 8 characters.");
      } else {
        dispatch(getAllCharacters(value));
        setHasSearched(true);
      }
    }, 700);

    setDebounceTimeout(newTimeout);
  };

  const handleSubmit = async () => {
    if (query.length !== 8) {
      setErrorMsg("Membership ID must be exactly 8 characters.");
    } else if (!characterId) {
      toast.error("Please select the character");
    } else {
      const payload = {
        gameId: id,
        membershipId: query,
        characterId,
      };

      const response = await dispatch(addParticipants(payload));
      if (response?.payload?.code === 200) {
        resetStateOnClear();
        onClose();
        dispatch(getAllParticipants(participateId));
        toast.success("Participant added sucessfully!");
      } else if (
        response?.payload?.code >= 400 &&
        response?.payload?.code < 500
      ) {
        toast.error(response?.payload?.message);
      } else {
        toast.error("Something went wrong!");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-[#150c3f] p-6 rounded-lg shadow-lg text-center w-full max-w-[460px]">
        <button
          className="absolute top-0 right-0 text-white text-lg p-2"
          onClick={onClose}
        >
          <RxCross1 />
        </button>
        <h3 className="text-white text-2xl mb-4">Add Participant</h3>
        <input
          type="text"
          placeholder="Enter Membership ID"
          value={query}
          className="mt-1 block w-full text-white px-3 py-2 bg-[#372F5B] rounded-md focus:outline-none focus:ring focus:ring-red-500 focus:ring-opacity-50 h-10"
          onChange={handleChange}
        />
        {errorMsg && <p className="text-red-400 text-sm mt-3">{errorMsg}</p>}

        <div className="max-h-[500px] overflow-y-auto mt-5 no-scrollbar ">
          {isLoading ? (
            <p className="text-white my-5">Searching...</p>
          ) : hasSearched ? (
            characters?.data?.length > 0 ? (
              characters?.data?.map((character) => (
                <div
                  className="flex gap-3 text-white mt-4 p-4 rounded-[10px] cursor-pointer  bg-[#372F5B]"
                  key={character?._id}
                >
                  <img
                    src={
                      character?.avatar || "/assets/images/character-image.jpeg"
                    }
                    alt={character?.name}
                    className="min-w-[50px] h-[50px] rounded-full"
                  />
                  <div className="w-full text-left">
                    <h2 className="font-semibold">{character?.name}</h2>
                    <h2 className="text-sm">
                      {character?.archeType} | {character?.class}
                    </h2>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="block text-sm">
                        Level {character?.level}
                      </span>
                      <div className="flex items-center gap-2">
                        {Array.from({ length: character?.points })?.map(
                          (_, index) => {
                            return (
                              <FaCircle
                                key={index + 1}
                                className="text-yellow-300 text-[12px]"
                              />
                            );
                          }
                        )}
                        {Array.from({
                          length: totalCharacterPoint - character?.points,
                        })?.map((_, index) => {
                          return (
                            <FaRegCircle
                              key={index + 1}
                              className="text-[12px]"
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={characterId === character?._id}
                    onChange={() => {
                      if (characterId === character._id) {
                        setCharacterId("");
                      } else {
                        setCharacterId(character._id);
                      }
                    }}
                    className="ml-auto w-5 h-4 appearance-none border border-white rounded-sm bg-[#372F5B] checked:bg-red-500 checked:border-red-500"
                  />
                </div>
              ))
            ) : (
              <p className="text-white my-5">
                No characters found for this ID.
              </p>
            )
          ) : null}
        </div>
        <button
          className="bg-red-600 px-2 mt-5 py-3 w-full text-white rounded-lg"
          type="button"
          onClick={handleSubmit}
          disabled={participantsLoading}
        >
          Submit
        </button>
        {participantsLoading && (
          <div className="loader">
            <LineWave color={"#fff"} loading={isLoading} size={150} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantPopup;
